import React, { useState, useCallback } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapIntegrationLogItem } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { RotateCcw, Code, RefreshCw } from 'lucide-react';
import { SapPayloadInspectorModal } from '@/components/sap/SapPayloadInspectorModal';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

export const SapLogsPage: React.FC = () => {
  const [selectedLog, setSelectedLog] = useState<SapIntegrationLogItem | null>(null);

  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      const res = await mockSapSimulatorService.getLogs({
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      return res;
    },
    []
  );

  const {
    items: logs,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    total,
    sentinelRef,
    retry,
  } = useInfiniteScroll<SapIntegrationLogItem>({
    fetchFn,
    pageSize: 25,
  });

  const columns: ColumnDef<SapIntegrationLogItem>[] = [
    {
      header: 'BAPI / Interface Name',
      accessorKey: 'apiName',
      cell: (row) => <span className="font-mono font-extrabold text-primary text-xs">{row.apiName || 'SAP_BAPI_SYNC'}</span>,
    },
    {
      header: 'ProcureAI Ref',
      accessorKey: 'requestRef',
      cell: (row) => <span className="font-bold text-xs text-foreground">{row.requestRef}</span>,
    },
    {
      header: 'Correlation ID',
      accessorKey: 'correlationId',
      cell: (row) => <span className="font-mono text-[11px] text-foreground-muted">{row.correlationId}</span>,
    },
    {
      header: 'Duration',
      accessorKey: 'durationMs',
      cell: (row) => <span className="font-mono text-xs text-foreground font-semibold">{row.durationMs}ms</span>,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <span
          className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
            row.status === 'SUCCESS' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-rose-500/10 text-rose-700'
          }`}
        >
          {row.status} {row.retryCount > 0 && `(Retry x${row.retryCount})`}
        </span>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedLog(row)}
            className="h-7 text-[10px] font-bold rounded-lg"
          >
            <Code className="h-3 w-3 mr-1" /> Payload
          </Button>

          {row.status === 'FAILED' && (
            <Button
              size="sm"
              onClick={() => retry()}
              className="h-7 text-[10px] font-bold rounded-lg bg-rose-600 hover:bg-rose-700 text-white"
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Retry
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div>
          <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" /> Integration Audit Logs & Retry Queue
          </h2>
          <p className="text-xs text-foreground-secondary font-medium">
            Chronological audit stream of BAPI calls, correlation references, JSON payload inspection, and retry execution across 500+ records.
          </p>
        </div>

        <DataTable
          data={logs}
          columns={columns}
          isLoading={isLoading}
          pageSize={25}
          totalCount={total}
          infiniteScroll={true}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          sentinelRef={sentinelRef}
        />

        <SapPayloadInspectorModal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          logItem={selectedLog}
        />
      </div>
    </SAPLayout>
  );
};
